import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../services/api';

// Types for the enhanced API response
interface TimeSeriesEntry {
  period: string;
  totalRejections: number;
  totalAttempts: number;
  successCount: number;
  successRate: number;
  reasons: Record<string, number>;
  userCount: number;
}

interface UserBreakdown {
  userId: string;
  userEmail: string;
  userName: string;
  totalRejections: number;
  totalAttempts: number;
  successCount: number;
  successRate: number;
  topReasons: Record<string, number>;
}

interface Recommendation {
  type: 'warning' | 'suggestion' | 'info';
  title: string;
  description: string;
  action?: string;
  metric?: string;
}

interface FilterOption {
  id: string;
  email: string;
  name: string;
}

interface SectorStatus {
  sector: string;
  currentValueUsd: number;
  currentPct: number;
  maxPct: number;
  headroomPct: number;
  headroomValueUsd: number;
  positionCount: number;
  positions: string[];
  userCount: number;
  status: 'blocked' | 'warning' | 'ok';
}

interface LiveSectorData {
  totalPortfolioValueUsd: number;
  maxPctPerSector: number;
  accountCount: number;
  positionCount: number;
  sectors: SectorStatus[];
  blockedSectors: string[];
  warningSectors: string[];
  timestamp: string;
}

interface RejectionAnalyticsData {
  aggregated: {
    totalRejections: number;
    totalAttempts: number;
    totalSuccess: number;
    overallSuccessRate: number;
    rejectionReasons: Record<string, number>;
    lastRunTime?: string | null;
    daysSinceLastRun?: number | null;
    uniqueUsers: number;
  };
  timeSeries: TimeSeriesEntry[];
  userBreakdown: UserBreakdown[];
  recommendations: Recommendation[];
  filters: {
    availableUsers: FilterOption[];
    availableReasons: string[];
    granularityOptions: string[];
  };
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  recentLogs: Array<{
    id: string;
    timestamp: string;
    userId: string;
    userEmail?: string;
    userName?: string;
    symbol?: string;
    entryScore?: number;
    rejectionReason?: string;
    eventData?: any;
  }>;
}

type TabType = 'overview' | 'trends' | 'users' | 'logs';
type GranularityType = 'hourly' | 'daily' | 'weekly';

/**
 * Enhanced Rejection Analytics Dashboard Component
 * Admin-only view of trade rejection statistics with charts, filters, and recommendations
 */
const RejectionAnalytics: React.FC = () => {
  // Data state
  const [data, setData] = useState<RejectionAnalyticsData | null>(null);
  const [liveSectorData, setLiveSectorData] = useState<LiveSectorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [granularity, setGranularity] = useState<GranularityType>('daily');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Fetch main analytics data
  const fetchAnalytics = useCallback(async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);
      if (selectedUser) params.append('userId', selectedUser);
      if (selectedReason) params.append('reason', selectedReason);
      params.append('granularity', granularity);
      params.append('page', page.toString());
      params.append('pageSize', size.toString());

      const response = await api.get(`/admin/rejection-analytics?${params.toString()}`);
      setData(response.data.data);
      setCurrentPage(page);
      setPageSize(size);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to load rejection analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedUser, selectedReason, granularity, currentPage, pageSize]);

  // Fetch live sector status
  const fetchLiveSectorStatus = useCallback(async () => {
    try {
      setSectorLoading(true);
      const response = await api.get('/admin/rejection-analytics/live-sector-status');
      setLiveSectorData(response.data.data);
    } catch (e: any) {
      console.error('Failed to fetch live sector status:', e);
    } finally {
      setSectorLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchAnalytics(1, pageSize);
  }, [dateRange, selectedUser, selectedReason, granularity]);

  // Fetch live sector data on mount and when tab changes
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchLiveSectorStatus();
    }
  }, [activeTab, fetchLiveSectorStatus]);

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    fetchAnalytics(newPage, pageSize);
  };

  const handlePageSizeChange = (newSize: number) => {
    fetchAnalytics(1, newSize);
  };

  // Export functionality
  const exportData = (format: 'csv' | 'json') => {
    if (!data) return;

    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(data, null, 2);
      filename = `rejection-analytics-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    } else {
      // CSV export
      const headers = ['Date', 'Attempts', 'Rejections', 'Success', 'Success Rate', 'Top Reason'];
      const rows = data.timeSeries.map(entry => {
        const topReason = Object.entries(entry.reasons).sort(([, a], [, b]) => b - a)[0];
        return [
          entry.period,
          entry.totalAttempts,
          entry.totalRejections,
          entry.successCount,
          `${entry.successRate.toFixed(1)}%`,
          topReason ? topReason[0] : '-'
        ].join(',');
      });
      content = [headers.join(','), ...rows].join('\n');
      filename = `rejection-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // Format reason for display (explicit labels for known keys; fallback to title case)
  const formatReason = (reason: string): string => {
    const labels: Record<string, string> = {
      insufficient_buying_power: 'Insufficient buying power',
      quantity_zero: 'Insufficient buying power', // legacy, same meaning
      order_placement_failed: 'Order placement failed',
      sector_seat_allocation: 'Sector seat allocation limit',
      entry_score_too_low: 'Entry score too low',
      low_conf_regime_blocked: 'LOW confidence blocked (regime)',
      low_conf_regime_not_allowed: 'LOW confidence not allowed (regime)',
      low_conf_daily_limit: 'LOW confidence daily limit reached',
      low_conf_spread_too_wide: 'LOW confidence spread too wide',
      low_conf_liquidity_too_low: 'LOW confidence liquidity too low',
      low_conf_lunch_hour_blocked: 'LOW confidence blocked (lunch hour)',
      sector_concentration: 'Sector concentration limit',
      execution_error: 'Execution error',
    };
    return labels[reason] ?? reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Calculate top rejection reasons
  const topReasons = useMemo(() => {
    if (!data?.aggregated.rejectionReasons) return [];
    return Object.entries(data.aggregated.rejectionReasons)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  }, [data]);

  // Loading state
  if (loading && !data) {
    return (
      <div className="card bg-neutral-900 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          <p className="text-neutral-400">Loading rejection analytics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="card bg-neutral-900 p-6">
        <p className="text-red-400">Error: {error}</p>
        <button onClick={() => void fetchAnalytics()} className="btn btn-outline mt-4">
          Retry
        </button>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="card bg-neutral-900 p-6">
        <p className="text-neutral-400">No rejection analytics data available</p>
      </div>
    );
  }

  const { aggregated, timeSeries, userBreakdown, recommendations, recentLogs, filters } = data;

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="card bg-neutral-900 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-white mb-2">Rejection Analytics</h2>
            <p className="text-neutral-400">Track why trades are being rejected and identify optimization opportunities</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Export Button */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn btn-outline flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-neutral-800 border border-neutral-700 rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => exportData('csv')}
                    className="w-full px-4 py-2 text-left text-white hover:bg-neutral-700 rounded-t-lg"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => exportData('json')}
                    className="w-full px-4 py-2 text-left text-white hover:bg-neutral-700 rounded-b-lg"
                  >
                    Export as JSON
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                fetchAnalytics(currentPage, pageSize);
                fetchLiveSectorStatus();
              }}
              className="btn btn-outline flex items-center gap-2"
              disabled={loading}
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Warning Banner */}
        {aggregated.daysSinceLastRun !== null && aggregated.daysSinceLastRun !== undefined && aggregated.daysSinceLastRun > 7 && (
          <div className="mb-6 bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-yellow-300 font-semibold">Automated Trading Not Running</p>
                <p className="text-yellow-200 text-sm mt-1">
                  Last rejection data was logged <strong>{aggregated.daysSinceLastRun} days ago</strong>
                  {aggregated.lastRunTime && ` on ${new Date(aggregated.lastRunTime).toLocaleString()}`}.
                  Rejection analytics are only generated when automated trading is active.
                </p>
                <p className="text-yellow-200 text-sm mt-2">
                  <strong>Action Required:</strong> Enable automated trading for at least one user to collect rejection data.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start || ''}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end || ''}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">User</label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white"
            >
              <option value="">All Users</option>
              {filters.availableUsers.map(user => (
                <option key={user.id} value={user.id}>{user.name || user.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Rejection Reason</label>
            <select
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white"
            >
              <option value="">All Reasons</option>
              {filters.availableReasons.map(reason => (
                <option key={reason} value={reason}>{formatReason(reason)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Granularity</label>
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as GranularityType)}
              className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        {(dateRange.start || dateRange.end || selectedUser || selectedReason) && (
          <button
            onClick={() => {
              setDateRange({});
              setSelectedUser('');
              setSelectedReason('');
            }}
            className="mt-4 text-sm text-blue-400 hover:text-blue-300"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-neutral-800">
        {(['overview', 'trends', 'users', 'logs'] as TabType[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="card bg-neutral-900 p-5">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Total Rejections</h3>
              <p className="text-3xl font-bold text-red-400">{aggregated.totalRejections.toLocaleString()}</p>
              <p className="text-xs text-neutral-500 mt-1">
                {aggregated.totalAttempts > 0
                  ? `${((aggregated.totalRejections / aggregated.totalAttempts) * 100).toFixed(1)}% of attempts`
                  : 'No attempts'}
              </p>
            </div>

            <div className="card bg-neutral-900 p-5">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Total Attempts</h3>
              <p className="text-3xl font-bold text-blue-400">{aggregated.totalAttempts.toLocaleString()}</p>
              <p className="text-xs text-neutral-500 mt-1">All trade attempts</p>
            </div>

            <div className="card bg-neutral-900 p-5">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Success Rate</h3>
              <p className={`text-3xl font-bold ${aggregated.overallSuccessRate >= 50 ? 'text-green-400' : aggregated.overallSuccessRate >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
                {aggregated.overallSuccessRate.toFixed(1)}%
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                {aggregated.totalSuccess.toLocaleString()} successful
              </p>
            </div>

            <div className="card bg-neutral-900 p-5">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Unique Reasons</h3>
              <p className="text-3xl font-bold text-yellow-400">{Object.keys(aggregated.rejectionReasons || {}).length}</p>
              <p className="text-xs text-neutral-500 mt-1">Reasons tracked</p>
            </div>

            <div className="card bg-neutral-900 p-5">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Active Users</h3>
              <p className="text-3xl font-bold text-purple-400">{aggregated.uniqueUsers}</p>
              <p className="text-xs text-neutral-500 mt-1">With rejection data</p>
            </div>
          </div>

          {/* Recommendations Panel */}
          {recommendations.length > 0 && (
            <div className="card bg-neutral-900 p-6">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Recommendations
              </h3>
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      rec.type === 'warning'
                        ? 'bg-red-900/20 border-red-500/30'
                        : rec.type === 'suggestion'
                        ? 'bg-yellow-900/20 border-yellow-500/30'
                        : 'bg-blue-900/20 border-blue-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-full ${
                        rec.type === 'warning' ? 'bg-red-500/20' : rec.type === 'suggestion' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                      }`}>
                        {rec.type === 'warning' ? (
                          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        ) : rec.type === 'suggestion' ? (
                          <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{rec.title}</h4>
                        <p className="text-neutral-300 text-sm mt-1">{rec.description}</p>
                        {rec.action && (
                          <p className="text-neutral-400 text-xs mt-2 font-mono">{rec.action}</p>
                        )}
                        {rec.metric && (
                          <span className="inline-block mt-2 px-2 py-1 bg-neutral-800 rounded text-xs text-neutral-300">
                            {rec.metric}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Two-column layout for reasons and sector status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Rejection Reasons */}
            <div className="card bg-neutral-900 p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Top Rejection Reasons</h3>
              <div className="space-y-4">
                {topReasons.length > 0 ? (
                  topReasons.map(([reason, count], index) => {
                    const percentage = aggregated.totalRejections > 0
                      ? ((count / aggregated.totalRejections) * 100).toFixed(1)
                      : '0';
                    return (
                      <div key={reason}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-neutral-500 text-sm w-5">#{index + 1}</span>
                            <span className="text-white text-sm cursor-help" title={`Reason key: ${reason}`}>
                              {formatReason(reason)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-white font-semibold text-sm">{count.toLocaleString()}</span>
                            <span className="text-neutral-400 text-sm w-14 text-right">{percentage}%</span>
                          </div>
                        </div>
                        <div className="w-full bg-neutral-800 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-neutral-400">No rejection data available</p>
                )}
              </div>
            </div>

            {/* Live Sector Status */}
            <div className="card bg-neutral-900 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">Live Sector Status</h3>
                {liveSectorData && (
                  <span className="text-xs text-neutral-500">
                    Updated: {new Date(liveSectorData.timestamp).toLocaleTimeString()}
                  </span>
                )}
              </div>
              
              {sectorLoading ? (
                <div className="flex items-center gap-2 text-neutral-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span className="text-sm">Loading sector data...</span>
                </div>
              ) : liveSectorData ? (
                <div className="space-y-3">
                  {/* Blocked/Warning Summary */}
                  {(liveSectorData.blockedSectors.length > 0 || liveSectorData.warningSectors.length > 0) && (
                    <div className="flex gap-2 mb-4">
                      {liveSectorData.blockedSectors.length > 0 && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                          {liveSectorData.blockedSectors.length} blocked
                        </span>
                      )}
                      {liveSectorData.warningSectors.length > 0 && (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                          {liveSectorData.warningSectors.length} near limit
                        </span>
                      )}
                    </div>
                  )}
                  
                  {liveSectorData.sectors.slice(0, 6).map(sector => (
                    <div key={sector.sector} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        sector.status === 'blocked' ? 'bg-red-500' : 
                        sector.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm truncate">{sector.sector}</span>
                          <span className={`text-sm font-medium ${
                            sector.status === 'blocked' ? 'text-red-400' : 
                            sector.status === 'warning' ? 'text-yellow-400' : 'text-neutral-300'
                          }`}>
                            {sector.currentPct.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-neutral-800 rounded-full h-1.5 mt-1">
                          <div
                            className={`h-1.5 rounded-full ${
                              sector.status === 'blocked' ? 'bg-red-500' : 
                              sector.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, (sector.currentPct / sector.maxPct) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-3 border-t border-neutral-800 text-xs text-neutral-500">
                    Total: ${liveSectorData.totalPortfolioValueUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} across {liveSectorData.positionCount} positions
                  </div>
                </div>
              ) : (
                <p className="text-neutral-400 text-sm">Unable to load live sector data</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="space-y-6">
          {/* Trend Chart (Simple bar representation) */}
          <div className="card bg-neutral-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              Rejection Trend ({granularity.charAt(0).toUpperCase() + granularity.slice(1)})
            </h3>
            
            {timeSeries.length > 0 ? (
              <div className="space-y-2">
                {/* Chart Legend */}
                <div className="flex items-center gap-6 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-neutral-400">Success</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                    <span className="text-neutral-400">Rejections</span>
                  </div>
                </div>

                {/* Scrollable chart container */}
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    {timeSeries.slice(0, 14).reverse().map((entry) => {
                      const maxAttempts = Math.max(...timeSeries.slice(0, 14).map(e => e.totalAttempts));
                      const barWidth = maxAttempts > 0 ? (entry.totalAttempts / maxAttempts) * 100 : 0;
                      const successWidth = entry.totalAttempts > 0 ? (entry.successCount / entry.totalAttempts) * barWidth : 0;
                      const rejectWidth = barWidth - successWidth;

                      return (
                        <div key={entry.period} className="flex items-center gap-4 py-2">
                          <div className="w-24 text-xs text-neutral-400 text-right">
                            {granularity === 'hourly' 
                              ? new Date(entry.period).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })
                              : new Date(entry.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                          <div className="flex-1 flex h-6 bg-neutral-800 rounded overflow-hidden">
                            <div
                              className="bg-green-500 h-full transition-all"
                              style={{ width: `${successWidth}%` }}
                              title={`${entry.successCount} successful`}
                            />
                            <div
                              className="bg-red-500 h-full transition-all"
                              style={{ width: `${rejectWidth}%` }}
                              title={`${entry.totalRejections} rejected`}
                            />
                          </div>
                          <div className="w-20 text-xs text-neutral-400 text-right">
                            {entry.totalAttempts} attempts
                          </div>
                          <div className="w-16 text-xs text-right">
                            <span className={entry.successRate >= 50 ? 'text-green-400' : entry.successRate >= 20 ? 'text-yellow-400' : 'text-red-400'}>
                              {entry.successRate.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-neutral-400">No trend data available for the selected period</p>
            )}
          </div>

          {/* Time Series Table */}
          <div className="card bg-neutral-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Detailed Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-300 border-b border-neutral-800">
                    <th className="py-3 pr-4">Period</th>
                    <th className="py-3 pr-4 text-right">Attempts</th>
                    <th className="py-3 pr-4 text-right">Rejections</th>
                    <th className="py-3 pr-4 text-right">Success</th>
                    <th className="py-3 pr-4 text-right">Rate</th>
                    <th className="py-3 pr-4 text-right">Users</th>
                    <th className="py-3">Top Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {timeSeries.slice(0, 20).map(entry => {
                    const topReason = Object.entries(entry.reasons).sort(([, a], [, b]) => b - a)[0];
                    return (
                      <tr key={entry.period}>
                        <td className="py-3 pr-4 text-white">
                          {granularity === 'hourly'
                            ? new Date(entry.period).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })
                            : new Date(entry.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-3 pr-4 text-neutral-300 text-right">{entry.totalAttempts.toLocaleString()}</td>
                        <td className="py-3 pr-4 text-red-400 text-right">{entry.totalRejections.toLocaleString()}</td>
                        <td className="py-3 pr-4 text-green-400 text-right">{entry.successCount.toLocaleString()}</td>
                        <td className={`py-3 pr-4 text-right font-medium ${
                          entry.successRate >= 50 ? 'text-green-400' : entry.successRate >= 20 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {entry.successRate.toFixed(1)}%
                        </td>
                        <td className="py-3 pr-4 text-neutral-300 text-right">{entry.userCount}</td>
                        <td className="py-3 text-neutral-300">
                          {topReason ? (
                            <span title={`Reason key: ${topReason[0]}`}>
                              {formatReason(topReason[0])}
                              <span className="text-neutral-500 ml-1">({topReason[1]})</span>
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* User Breakdown Table */}
          <div className="card bg-neutral-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Per-User Breakdown</h3>
            
            {userBreakdown.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-300 border-b border-neutral-800">
                      <th className="py-3 pr-4">User</th>
                      <th className="py-3 pr-4 text-right">Attempts</th>
                      <th className="py-3 pr-4 text-right">Rejections</th>
                      <th className="py-3 pr-4 text-right">Success</th>
                      <th className="py-3 pr-4 text-right">Rate</th>
                      <th className="py-3">Top Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {userBreakdown.map(user => {
                      const topReason = Object.entries(user.topReasons).sort(([, a], [, b]) => b - a)[0];
                      return (
                        <tr key={user.userId} className="hover:bg-neutral-800/50">
                          <td className="py-3 pr-4">
                            <div>
                              <p className="text-white font-medium">{user.userName}</p>
                              <p className="text-neutral-500 text-xs">{user.userEmail}</p>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-neutral-300 text-right">{user.totalAttempts.toLocaleString()}</td>
                          <td className="py-3 pr-4 text-red-400 text-right">{user.totalRejections.toLocaleString()}</td>
                          <td className="py-3 pr-4 text-green-400 text-right">{user.successCount.toLocaleString()}</td>
                          <td className={`py-3 pr-4 text-right font-medium ${
                            user.successRate >= 50 ? 'text-green-400' : user.successRate >= 20 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {user.successRate.toFixed(1)}%
                          </td>
                          <td className="py-3 text-neutral-300">
                            {topReason ? (
                              <span className="inline-flex items-center gap-2" title={`Reason key: ${topReason[0]}`}>
                                <span className="px-2 py-0.5 bg-neutral-800 rounded text-xs">
                                  {formatReason(topReason[0])}
                                </span>
                                <span className="text-neutral-500 text-xs">{topReason[1]}x</span>
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-neutral-400">No user data available</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-6">
          {/* Recent Rejection Logs */}
          <div className="card bg-neutral-900 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Recent Rejection Logs</h3>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-cyan-500 rounded"></span>
                  <span className="text-neutral-400">Individual Trade</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-orange-500 rounded"></span>
                  <span className="text-neutral-400">Cycle Summary</span>
                </div>
              </div>
            </div>
            
            {recentLogs.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-300 border-b border-neutral-800">
                        <th className="py-3 pr-4">Type</th>
                        <th className="py-3 pr-4">Timestamp</th>
                        <th className="py-3 pr-4">User</th>
                        <th className="py-3 pr-4">Symbol</th>
                        <th className="py-3 pr-4">Entry Score</th>
                        <th className="py-3">Reason</th>
                        <th className="py-3 pr-4 text-right">Stats</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {recentLogs.map(log => {
                        const eventData = log.eventData || {};
                        const isIndividual = eventData.isIndividualRejection === true;
                        const symbol = log.symbol || eventData.symbol || null;
                        const entryScore = log.entryScore ?? eventData.entryScore ?? null;
                        
                        let rejectionReason: string | null = null;
                        let rejectionCount: number | null = null;
                        
                        if (log.rejectionReason) {
                          rejectionReason = log.rejectionReason;
                          rejectionCount = 1;
                        } else if (eventData.rejectionReason) {
                          rejectionReason = eventData.rejectionReason;
                          rejectionCount = 1;
                        } else if (eventData.rejectionReasons && Object.keys(eventData.rejectionReasons).length > 0) {
                          const topReason = Object.entries(eventData.rejectionReasons)
                            .sort(([, a], [, b]) => (b as number) - (a as number))[0];
                          if (topReason) {
                            rejectionReason = topReason[0];
                            rejectionCount = topReason[1] as number;
                          }
                        }
                        
                        const userDisplay = log.userName
                          ? `${log.userName} (${log.userEmail || ''})`.trim().replace(/\(\)$/, '').trim()
                          : log.userEmail || log.userId;
                        
                        const totalRejections = eventData.totalRejections;
                        const successCount = eventData.successCount;
                        const totalAttempts = eventData.totalAttempts;
                        
                        return (
                          <tr key={log.id} className={isIndividual ? 'bg-cyan-500/5' : 'bg-orange-500/5'}>
                            <td className="py-3 pr-4">
                              {isIndividual ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-medium">
                                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span>
                                  Trade
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 text-xs font-medium">
                                  <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                                  Cycle
                                </span>
                              )}
                            </td>
                            <td className="py-3 pr-4 text-neutral-300">
                              {new Date(log.timestamp).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="py-3 pr-4 text-white">
                              <div className="max-w-xs truncate" title={userDisplay}>
                                {userDisplay}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              {symbol ? (
                                <span className="text-cyan-400 font-semibold">{symbol}</span>
                              ) : (
                                <span className="text-neutral-600 text-xs">-</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              {entryScore !== null ? (
                                <span className="text-neutral-300 font-mono">{entryScore.toFixed(2)}</span>
                              ) : (
                                <span className="text-neutral-600 text-xs">-</span>
                              )}
                            </td>
                            <td className="py-3">
                              {rejectionReason ? (
                                <span
                                  className="text-red-400"
                                  title={eventData.rejectionMessage ? String(eventData.rejectionMessage) : undefined}
                                >
                                  {formatReason(rejectionReason)}
                                  {rejectionCount && rejectionCount > 1 && (
                                    <span className="ml-1 text-xs text-neutral-500">x{rejectionCount}</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-neutral-600 text-xs">N/A</span>
                              )}
                            </td>
                            <td className="py-3 pr-4 text-right">
                              {!isIndividual && totalAttempts !== undefined ? (
                                <div className="text-xs text-neutral-400">
                                  <div>{successCount || 0} / {totalRejections || 0}</div>
                                  <div className="text-neutral-600">{totalAttempts} total</div>
                                </div>
                              ) : (
                                <span className="text-neutral-600 text-xs">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {data.pagination.totalPages > 1 && (
                  <div className="mt-6 pt-4 border-t border-neutral-800">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="text-sm text-neutral-400">
                          Showing {((data.pagination.currentPage - 1) * data.pagination.pageSize) + 1} to{' '}
                          {Math.min(data.pagination.currentPage * data.pagination.pageSize, data.pagination.totalCount)} of{' '}
                          {data.pagination.totalCount} entries
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-neutral-400">Show:</label>
                          <select
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                            className="bg-neutral-800 border border-neutral-700 rounded px-2 py-1 text-white text-sm"
                          >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={!data.pagination.hasPrevPage}
                          className="px-3 py-1 rounded bg-neutral-800 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors"
                        >
                          First
                        </button>
                        <button
                          onClick={() => handlePageChange(data.pagination.currentPage - 1)}
                          disabled={!data.pagination.hasPrevPage}
                          className="px-3 py-1 rounded bg-neutral-800 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors"
                        >
                          Previous
                        </button>

                        <div className="flex items-center gap-1">
                          {[...Array(Math.min(5, data.pagination.totalPages))].map((_, i) => {
                            let pageNum;
                            if (data.pagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (data.pagination.currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (data.pagination.currentPage >= data.pagination.totalPages - 2) {
                              pageNum = data.pagination.totalPages - 4 + i;
                            } else {
                              pageNum = data.pagination.currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  pageNum === data.pagination.currentPage
                                    ? 'bg-blue-600 text-white font-semibold'
                                    : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => handlePageChange(data.pagination.currentPage + 1)}
                          disabled={!data.pagination.hasNextPage}
                          className="px-3 py-1 rounded bg-neutral-800 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => handlePageChange(data.pagination.totalPages)}
                          disabled={!data.pagination.hasNextPage}
                          className="px-3 py-1 rounded bg-neutral-800 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-neutral-400 text-center py-8">
                No rejection logs available for the selected filters
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RejectionAnalytics;
